import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Circle, Rocket } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/use-auth'
import { Page, PageHeader, Card, KpiCard, StatusPill } from '@/components/rm'
import { cn } from '@/lib/utils'
import { phFormat, peso, manilaParts, manilaDayIso, manilaWallToIso } from '@/lib/ph'

export const Route = createFileRoute('/_app/dashboard')({ component: DashboardPage })

const money = (n: number) => peso(n, { whole: true })
const fmtTime = (iso: string | null) => (iso ? phFormat(new Date(iso), 'time', { hour: 'numeric', minute: '2-digit' }).replace(' ', '').toLowerCase() : null)
const greeting = () => { const h = manilaParts(new Date()).hour; return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening' }

interface KpiData {
  revenueMonth: number
  revenuePrev: number
  jobsToday: { id: number; name: string | null; status: string | null; planned_start_date: string | null; client?: string | null; address: string | null }[]
  quotesAwaiting: { count: number; value: number; rows: { id: number; client: string; total: number; status: string; sent_at: string | null }[] }
  overdue: { count: number; value: number }
}

function DashboardPage() {
  const { role } = useAuth()
  const isAdmin = role === 'company-admin'
  const navigate = useNavigate()
  const { firstName } = useAuth()

  const q = useQuery({
    queryKey: ['dashboard-kpis'],
    queryFn: async (): Promise<KpiData> => {
      // Day and month boundaries are Manila's (spec 001), whatever zone this device is on.
      const now = new Date()
      const today = manilaDayIso(now)
      const monthStart = manilaWallToIso(`${today.slice(0, 8)}01`)!
      const dayStart = manilaWallToIso(today)!
      const dayEnd = manilaWallToIso(today, '23:59:59')!
      const todayIso = now.toISOString()

      const m = manilaParts(now)
      const prev = m.month === 1 ? { y: m.year - 1, mo: 12 } : { y: m.year, mo: m.month - 1 }
      const lastMonthStart = manilaWallToIso(`${prev.y}-${String(prev.mo).padStart(2, '0')}-01`)!
      const [paid, prevPaid, jobsToday, quotes, invoices] = await Promise.all([
        supabase.from('customer_invoice').select('total, amount_paid, paid_at').is('deleted_at', null).gte('paid_at', monthStart),
        supabase.from('customer_invoice').select('amount_paid, total').is('deleted_at', null).gte('paid_at', lastMonthStart).lt('paid_at', monthStart),
        supabase.from('task').select('id, name, status, planned_start_date, address, contact:contact(first_name, last_name, organization)').eq('is_draft', false).is('deleted_at', null).gte('planned_start_date', dayStart).lte('planned_start_date', dayEnd).order('planned_start_date'),
        supabase.from('quote').select('id, total, status, sent_at, contact:contact(first_name, last_name, organization)').is('deleted_at', null).in('status', ['sent', 'viewed']).order('sent_at', { ascending: false }),
        supabase.from('customer_invoice').select('total, amount_paid, due_date, status').is('deleted_at', null).not('status', 'in', '(paid,void,draft)').lt('due_date', todayIso),
      ])

      const cname = (c: { first_name?: string | null; last_name?: string | null; organization?: string | null } | null) =>
        c ? ([c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.organization || '—') : '—'

      const revenueMonth = (paid.data ?? []).reduce((s, r) => s + Number(r.amount_paid ?? r.total ?? 0), 0)
      const revenuePrev = (prevPaid.data ?? []).reduce((s, r) => s + Number(r.amount_paid ?? r.total ?? 0), 0)
      const qRows = (quotes.data ?? []).map((r) => {
        const x = r as { id: number; total: number; status: string; sent_at: string | null; contact: never }
        return { id: x.id, total: Number(x.total ?? 0), status: x.status, sent_at: x.sent_at, client: cname(x.contact) }
      })
      const overdueRows = invoices.data ?? []
      return {
        revenueMonth,
        revenuePrev,
        jobsToday: (jobsToday.data ?? []).map((r) => { const x = r as unknown as { id: number; name: string | null; status: string | null; planned_start_date: string | null; address: string | null; contact: { first_name?: string | null; last_name?: string | null; organization?: string | null } | null }; return { id: x.id, name: x.name, status: x.status, planned_start_date: x.planned_start_date, address: x.address, client: cname(x.contact) } }),
        quotesAwaiting: { count: qRows.length, value: qRows.reduce((s, r) => s + r.total, 0), rows: qRows.slice(0, 4) },
        overdue: { count: overdueRows.length, value: overdueRows.reduce((s, r) => s + (Number(r.total ?? 0) - Number(r.amount_paid ?? 0)), 0) },
      }
    },
  })

  const onboarding = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: async () => {
      const head = { count: 'exact' as const, head: true }
      const [clients, quotes, prices, team] = await Promise.all([
        supabase.from('contact').select('id', head).is('deleted_at', null),
        supabase.from('quote').select('id', head).is('deleted_at', null),
        supabase.from('price_item').select('id', head).is('archived_at', null),
        supabase.from('users').select('id', head).is('deleted_at', null),
      ])
      return { clients: (clients.count ?? 0) > 0, quotes: (quotes.count ?? 0) > 0, prices: (prices.count ?? 0) > 0, team: (team.count ?? 0) > 1 }
    },
  })

  const d = q.data
  const dateLine = phFormat(new Date(), 'date', { weekday: 'long', day: 'numeric', month: 'long' })
  const inProgress = d?.jobsToday.filter((j) => (j.status ?? '').toLowerCase().includes('progress')).length ?? 0
  const upcoming = (d?.jobsToday.length ?? 0) - inProgress

  return (
    <Page>
      <PageHeader
        title={`${greeting()}, ${firstName || 'there'}`}
        subtitle={`${dateLine} · ${d?.jobsToday.length ?? 0} jobs scheduled${isAdmin ? ` · ${d?.quotesAwaiting.count ?? 0} quotes need follow-up` : ''}`}
      />

      {onboarding.data && <OnboardingChecklist status={onboarding.data} onGo={(to, search) => navigate({ to, search })} />}

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
        <KpiCard label="Revenue · this month" value={d ? money(d.revenueMonth) : '—'} delta={
          d && d.revenuePrev > 0
            ? (() => { const pct = Math.round(((d.revenueMonth - d.revenuePrev) / d.revenuePrev) * 100)
                return <span className={pct >= 0 ? 'text-token-success' : 'text-token-danger'}>{pct >= 0 ? '▲' : '▼'} {Math.abs(pct)}% vs last month</span> })()
            : <span className="text-token-secondary">{money(d?.revenuePrev ?? 0)} last month</span>
        } />
        <KpiCard label="Jobs today" value={d ? String(d.jobsToday.length) : '—'} delta={<span className="text-token-secondary">{inProgress} in progress · {Math.max(0, upcoming)} upcoming</span>} />
        {isAdmin && <KpiCard label="Quotes awaiting" value={d ? money(d.quotesAwaiting.value) : '—'} delta={<span className="text-token-warning">{d?.quotesAwaiting.count ?? 0} open quotes</span>} />}
        <KpiCard label="Overdue invoices" value={d ? money(d.overdue.value) : '—'} delta={<span className="text-token-danger">{d?.overdue.count ?? 0} invoices · chase</span>} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.5fr_1fr]">
        {/* Today's schedule */}
        <Card>
          <div className="flex items-center justify-between border-b border-token-subtle px-5 py-4">
            <span className="font-display text-[16px] font-bold text-token-primary">Today's schedule</span>
            <button onClick={() => navigate({ to: '/schedule' })} className="text-[13px] font-bold text-token-primary hover:text-token-primary">Open board →</button>
          </div>
          {q.isLoading ? (
            <div className="space-y-3 p-5">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-5 animate-pulse rounded bg-canvas-subtle" />)}</div>
          ) : (d?.jobsToday.length ?? 0) === 0 ? (
            <p className="px-5 py-10 text-center text-[13px] text-token-secondary">Nothing scheduled today.</p>
          ) : (
            <div className="divide-y divide-token-subtle">
              {d!.jobsToday.map((j) => {
                const prog = (j.status ?? '').toLowerCase().includes('progress')
                return (
                  <button key={j.id} onClick={() => navigate({ to: '/jobs/$id', params: { id: String(j.id) } })} className="flex w-full items-center gap-3.5 px-5 py-3.5 text-left transition-colors hover:bg-token-surface">
                    <span className="font-display w-[54px] flex-none text-[14px] font-bold text-token-secondary">{fmtTime(j.planned_start_date) ?? '—'}</span>
                    <span className={cn('h-2 w-2 flex-none rounded-full', prog ? 'bg-token-success' : 'bg-token-info')} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-bold text-token-primary">{j.name || 'Job'}</span>
                      <span className="block truncate text-[12px] text-token-secondary">{j.client}{j.address ? ` · ${j.address}` : ''}</span>
                    </span>
                    <StatusPill status={j.status || 'scheduled'} />
                  </button>
                )
              })}
            </div>
          )}
        </Card>

        {/* Right column */}
        <div className="space-y-4">
          {isAdmin && (
          <Card className="p-5">
            <p className="font-display mb-3.5 text-[15px] font-bold text-token-primary">Quotes to follow up</p>
            {(d?.quotesAwaiting.rows.length ?? 0) === 0 ? (
              <p className="py-4 text-center text-[13px] text-token-secondary">Nothing awaiting a decision.</p>
            ) : (
              <div className="space-y-3.5">
                {d!.quotesAwaiting.rows.map((qr) => {
                  const age = qr.sent_at ? Math.floor((Date.now() - new Date(qr.sent_at).getTime()) / 86400000) : null
                  const viewed = qr.status === 'viewed'
                  return (
                    <button key={qr.id} onClick={() => navigate({ to: '/quotes/$id', params: { id: String(qr.id) } })} className="flex w-full items-center justify-between text-left">
                      <div className="min-w-0">
                        <div className="truncate text-[13.5px] font-bold text-token-primary">{qr.client}</div>
                        <div className="text-[12px] text-token-secondary">Sent {age === null ? 'recently' : age <= 0 ? 'today' : age < 7 ? `${age} days ago` : '1 week ago'}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-[14px] font-bold tabular-nums text-token-primary">{money(qr.total)}</div>
                        <div className={cn('text-[11.5px] font-bold', viewed ? 'text-token-warning' : 'text-token-secondary')}>{viewed ? 'Viewed' : 'Not opened'}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </Card>
          )}

          {/* AI tip */}
          <div className="rounded-[16px] border border-token-warning bg-gradient-to-br from-token-warning-surface to-white p-5">
            <p className="mb-2 flex items-center gap-2 text-[14px] font-bold text-token-primary"><span className="text-[15px] text-token-success">✦</span> AI tip</p>
            <p className="text-[13.5px] leading-relaxed text-token-secondary">
              Optimise your team's routes from the <button onClick={() => navigate({ to: '/routes' })} className="font-bold text-token-primary underline decoration-accent decoration-2 underline-offset-2">Routes</button> board to cut backtracking and save drive time today.
            </p>
          </div>
        </div>
      </div>
    </Page>
  )
}

function OnboardingChecklist({ status, onGo }: { status: { clients: boolean; quotes: boolean; prices: boolean; team: boolean }; onGo: (to: string, search?: Record<string, string>) => void }) {
  // Deep-link the settings steps straight to their tab (spec 009 T002) instead of dumping the
  // user on the default Business-profile tab and making them hunt.
  const steps = [
    { done: status.clients, label: 'Add your first client', to: '/clients' as const, search: undefined },
    // The price list moved out of Settings to its own route; /settings?tab=pricing still redirects
    // here for old bookmarks, but the checklist should not take the detour.
    { done: status.prices, label: 'Build your price list', to: '/pricing' as const, search: undefined },
    { done: status.quotes, label: 'Send your first quote', to: '/quotes' as const, search: undefined },
    { done: status.team, label: 'Invite your team', to: '/settings' as const, search: { tab: 'team' } },
  ]
  const done = steps.filter((s) => s.done).length
  if (done === steps.length) return null
  const pct = Math.round((done / steps.length) * 100)
  return (
    <div className="mt-5 overflow-hidden rounded-[14px] border border-token-warning bg-gradient-to-br from-token-warning-surface to-white">
      <div className="flex items-center justify-between px-5 py-3.5">
        <span className="inline-flex items-center gap-2 text-[14px] font-bold text-token-primary"><Rocket className="h-4 w-4 text-token-primary" /> Get set up</span>
        <span className="text-[12.5px] font-bold text-token-warning">{done}/{steps.length} done</span>
      </div>
      <div className="h-1 w-full bg-token-selected"><div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} /></div>
      <div className="grid gap-1 p-2.5 sm:grid-cols-2">
        {steps.map((s) => (
          <button key={s.label} onClick={() => !s.done && onGo(s.to, s.search)} disabled={s.done} className="flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left transition-colors enabled:hover:bg-token-surface/70">
            {s.done ? <CheckCircle2 className="h-[18px] w-[18px] flex-none text-token-success" /> : <Circle className="h-[18px] w-[18px] flex-none text-token-warning" />}
            <span className={cn('text-[13.5px] font-semibold', s.done ? 'text-token-muted line-through' : 'text-token-primary')}>{s.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
